import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { REVENUE_GOALS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", revenue_goal: "", message: "" };

export const DiscoveryModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const handler = () => {
      setForm(EMPTY);
      setStatus("idle");
      setOpen(true);
    };
    window.addEventListener("open-discovery", handler);
    return () => window.removeEventListener("open-discovery", handler);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-[#F4F6F0] px-4 py-3 text-sm text-[#23271F] placeholder:text-[#9aa48d] outline-none transition-colors focus:border-brand/60";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="discovery-modal"
        className="max-h-[92vh] overflow-y-auto border-line bg-white p-8 text-[#23271F] sm:max-w-lg"
      >
        {status === "success" ? (
          <div data-testid="discovery-success" className="flex flex-col items-center py-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#5C8A1E]" />
            <DialogTitle className="mt-6 font-serif text-2xl font-medium text-[#1b1f17]">
              Thank you — request received.
            </DialogTitle>
            <DialogDescription className="mt-3 max-w-sm text-sm text-[#586150]">
              We'll be in touch within one business day to schedule your discovery call.
            </DialogDescription>
            <button
              data-testid="discovery-close"
              onClick={() => setOpen(false)}
              className="mt-8 rounded-full bg-brand px-7 py-3 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-hover"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogTitle className="font-serif text-2xl font-medium text-[#1b1f17]">
              Book a Discovery Call
            </DialogTitle>
            <DialogDescription className="text-sm text-[#586150]">
              Share a few details and we'll reach out to schedule your 30-minute GTM strategy audit —
              or to discuss our cross-functional recruitment services.
            </DialogDescription>

            <form data-testid="discovery-form" onSubmit={submit} className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input required data-testid="modal-input-name" className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} />
                <input required data-testid="modal-input-company" className={inputCls} placeholder="Company" value={form.company} onChange={update("company")} />
              </div>
              <input required type="email" data-testid="modal-input-email" className={inputCls} placeholder="Work email" value={form.email} onChange={update("email")} />
              <select required data-testid="modal-input-revenue-goal" className={`${inputCls} ${form.revenue_goal ? "text-[#23271F]" : "!text-[#9aa48d]"}`} value={form.revenue_goal} onChange={update("revenue_goal")}>
                <option value="" disabled>Revenue goal…</option>
                {REVENUE_GOALS.map((g) => (
                  <option key={g} value={g} className="text-[#23271F]">{g}</option>
                ))}
              </select>
              <textarea data-testid="modal-input-message" rows={3} className={inputCls} placeholder="What's the revenue challenge you're solving? (optional)" value={form.message} onChange={update("message")} />

              {status === "error" && (
                <p data-testid="discovery-error" className="text-sm text-red-500">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                data-testid="discovery-submit"
                disabled={status === "loading"}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-hover disabled:opacity-60"
              >
                {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Submit Request <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
